<template>
  <v-autocomplete
    data-private
    dense
    multiple
    clearable
    outlined
    ref="multipleSelect"
    item-color="white"
    :cache-items="cacheItems"
    :error="error"
    :hint="hint"
    :hide-details="hideDetails"
    :rules="rules"
    :required="required"
    :loading="loading"
    :disabled="disabled"
    :item-value="itemValue"
    :item-text="itemText"
    :menu-props="{ offsetY: true }"
    :items="options"
    :label="label"
    :persistent-hint="persistentHint"
    :placeholder="$t('menu.selection.select_an_option')"
    :prepend-icon="icon"
    :value="value"
    :search-input.sync="searchTerm"
    @change="handleChange"
  >
    <template v-if="showSelectAll" slot="prepend-item">
      <v-checkbox
        v-if="!searchTerm && options.length > 2"
        dense
        hide-details
        class="ma-0 pa-0 mt-n1 px-4 mb-1"
        :ripple="false"
        :value="selectedAll"
        @click="toggleSelectAll"
      >
        <template v-slot:label>
          <v-list-item dense class="ma-0 pa-0 ms-2">
            <v-list-item-title class="blue-grey--text text--darken-3">
              {{ $t('common.select_all') }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-checkbox>

      <v-divider />
    </template>

    <template v-slot:selection="{ item, index }">
      <v-chip
        v-if="index < maxChipsShown"
        class="mt-1"
        outlined
        small
        :ripple="false"
      >
        <span v-if="index < lastChip">{{ item[itemText] }}</span>
        <span v-if="index === lastChip">{{
          $t('common.plusOthersWithCount_other', {
            count: value.length - lastChip,
          })
        }}</span>
      </v-chip>
    </template>

    <template v-slot:item="data">
      <v-checkbox
        class="ma-0 pa-0 py-1"
        color="blue"
        hide-details
        :input-value="checkInputValue(data.item[itemValue])"
        :key="data.item[itemValue]"
        :ripple="false"
        :value="checkInputValue(data.item[itemValue])"
      >
        <template v-slot:label>
          <v-list-item-content class="mx-0 px-0 ms-2">
            <v-list-item-title
              class="mx-0 px-0 blue-grey--text text--darken-3 mb-1"
            >
              {{ data.item[itemText] }}
            </v-list-item-title>

            <v-list-item-subtitle
              class="font-size-12 blue-grey--text d-flex align-center"
            >
              <v-icon
                color="blue-grey"
                size="12"
                class="me-1"
                v-if="data.item.subtitleIcon"
                >{{ data.item.subtitleIcon }}</v-icon
              >
              {{ data.item.subtitle }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-checkbox>
    </template>
  </v-autocomplete>
</template>

<script>
const MAX_CHIPS_SHOWN = 4

import _ from 'lodash'

export default {
  name: 'MultipleSelect',

  props: {
    cacheItems: { type: Boolean, required: false, default: true },
    disabled: Boolean,
    error: Boolean,
    hideDetails: { type: Boolean, required: false, default: false },
    hint: { type: String, required: false },
    icon: { type: String, required: false },
    itemText: { type: String, required: false, default: 'name' },
    itemValue: { type: String, required: false, default: '_id' },
    label: { type: String, required: false },
    loading: { type: Boolean, required: false, default: false },
    maxChipsShown: { type: Number, required: false, default: MAX_CHIPS_SHOWN },
    options: { type: Array, required: true },
    persistentHint: { type: Boolean, required: false, default: false },
    required: { type: Boolean, required: false, default: false },
    showSelectAll: { type: Boolean, required: false, default: true },
    value: { type: Array, required: true },
  },

  data() {
    return {
      searchTerm: null,
    }
  },
  watch: {
    searchTerm: _.debounce(function(term) {
      this.$emit('search', term)
    }, 500),
  },

  computed: {
    lastChip() {
      return this.maxChipsShown - 1
    },

    cachedItems() {
      return this.$refs?.multipleSelect?.cachedItems?.length
        ? this.$refs?.multipleSelect?.cachedItems
        : this.options
    },

    selectedAll() {
      return this.value?.length === this.cachedItems?.length
    },

    rules() {
      if (!this.required) return []
      return [
        field =>
          Boolean(field.length) ||
          this.$t('validation.form.this_field_is_required'),
      ]
    },
  },

  methods: {
    checkInputValue(value) {
      const ids = this.value.map(item => item?.[this.itemValue] ?? item)
      return ids.includes(value)
    },

    handleChange(items) {
      this.searchTerm = ''

      this.$emit('change', items)
    },

    toggleSelectAll() {
      this.$nextTick(() => {
        if (this.selectedAll) {
          this.$emit('change', [])
        } else {
          const values =
            _.compact(
              this.cachedItems?.map?.(option => option?.[this.itemValue])
            ) ?? []
          this.$emit('change', values.slice())
        }
      })
    },
  },
}
</script>
